import { FileUpload, FileUploadState } from './types'

type AppendUploadsAction = {
  type: 'APPEND_UPLOADS'
  payload: {
    jobId: string
    files: Array<{ id: string; file: File }>
  }
}

type UpdateUploadProgressAction = {
  type: 'UPDATE_UPLOAD_PROGRESS'
  payload: {
    id: string
    progress: number
  }
}

type CompleteUploadAction = {
  type: 'COMPLETE_UPLOAD'
  payload: {
    id: string
  }
}

type FailUploadAction = {
  type: 'FAIL_UPLOAD'
  payload: {
    id: string
    error: string
  }
}

type FileUploadAction =
  | AppendUploadsAction
  | UpdateUploadProgressAction
  | CompleteUploadAction
  | FailUploadAction

function getOverallUploadProgress(files: Record<string, FileUpload>) {
  const uploads = Object.values(files)
  if (uploads.length === 0) return 0
  return Math.round(
    uploads.reduce((total, upload) => total + upload.progress, 0) / uploads.length,
  )
}

export function generateFileUploadId(opts: { jobId: string; name: string }) {
  return `${opts.jobId}.${opts.name}`
}

function calculateCompleted(files: Record<string, FileUpload>) {
  return Object.values(files).filter((file) => file.status === 'uploaded').length
}

export function fileUploadsReducer(
  state: FileUploadState,
  action: FileUploadAction,
): FileUploadState {
  switch (action.type) {
    case 'APPEND_UPLOADS': {
      const files = action.payload.files.reduce((acc, file) => {
        return {
          ...acc,
          [file.id]: {
            id: file.id,
            jobId: action.payload.jobId,
            fileName: file.file.name,
            status: 'uploading' as const,
            progress: 0,
          },
        }
      }, state.files)
      return {
        files,
        ids: Object.keys(files),
        completed: calculateCompleted(files),
        progress: getOverallUploadProgress(files),
      }
    }

    case 'UPDATE_UPLOAD_PROGRESS': {
      const existing = state.files[action.payload.id]
      if (!existing) return state

      const files = {
        ...state.files,
        [action.payload.id]: {
          ...existing,
          progress: action.payload.progress,
        },
      }

      return {
        ...state,
        files,
        progress: getOverallUploadProgress(files),
        completed: calculateCompleted(files),
      }
    }

    case 'COMPLETE_UPLOAD': {
      const existing = state.files[action.payload.id]
      if (!existing) return state

      const files = {
        ...state.files,
        [action.payload.id]: {
          ...existing,
          status: 'uploaded' as const,
          progress: 100,
        },
      }

      return {
        ...state,
        files,
        progress: getOverallUploadProgress(files),
        completed: calculateCompleted(files),
      }
    }

    case 'FAIL_UPLOAD': {
      const existing = state.files[action.payload.id]
      if (!existing) return state

      const files = {
        ...state.files,
        [action.payload.id]: {
          ...existing,
          status: 'error' as const,
          error: action.payload.error,
        },
      }

      return {
        ...state,
        files,
        progress: getOverallUploadProgress(files),
        completed: calculateCompleted(files),
      }
    }

    default:
      return state
  }
}
